import {
  useState,
} from 'react'
import {
  useQuery,
} from '@tanstack/react-query'
import {
  Link,
} from 'react-router-dom'
import type {
  FormEvent,
} from 'react'
import {
  CreditCard, Loader2, ExternalLink, Users, Tag,
} from 'lucide-react'
import {
  billingService,
  getApiErrorMessage,
} from '../services/api'
import type {
  AppliedDiscount,
} from '../services/api'
import {
  CheckoutModal,
} from './CheckoutModal'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  ACCOUNT_CARD_CLASS,
} from '../utils/cardStyles'
import {
  canApplyPromoCode,
  describeDiscount,
  isPastDue,
  PAST_DUE_NOTICE,
  PROMO_FAILED_FALLBACK,
} from '../utils/billing'

// The card shell, shared by all three branches below and with the Mail &
// Calendar card this one is stretched against — see `utils/cardStyles`.
const CARD_CLASS = ACCOUNT_CARD_CLASS

const BILLING_SUBSCRIPTION_QUERY_KEY = ['billing', 'subscription']

function StatusBadge({ status }: { status: string | null }): JSX.Element {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
    trialing: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Trial' },
    past_due: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Past Due' },
    canceled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Canceled' },
    expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expired' },
    incomplete: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Incomplete' },
  }

  const c = status ? config[status] : null
  if (!c) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
        No Subscription
      </span>
    )
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${c.bg} ${c.text} rounded-full`}>
      {c.label}
    </span>
  )
}

function PromoCodeForm(): JSX.Element {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [applied, setApplied] = useState<AppliedDiscount | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmed = code.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { discount } = await billingService.applyPromotionCode(trimmed)
      trackEvent(OnboardingEvents.PROMO_CODE_APPLIED, { code: discount.code })
      setApplied(discount)
    } catch (err) {
      setError(getApiErrorMessage(err, PROMO_FAILED_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  if (applied) {
    return (
      <div className="w-full bg-green-50 border border-green-200 rounded-lg p-3">
        <p className="text-sm text-green-800">
          <span className="font-medium">{applied.code}</span> applied — {describeDiscount(applied)}.
        </p>
      </div>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <Tag className="w-3.5 h-3.5" />
        Have a promo code?
      </button>
    )
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="w-full">
      <label htmlFor="promo-code" className="block text-sm font-medium text-gray-700 mb-1">
        Promo code
      </label>
      <div className="flex gap-2">
        <input
          id="promo-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          disabled={submitting}
          className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={submitting || !code.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 disabled:opacity-50"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Apply
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </form>
  )
}

export function BillingSection(): JSX.Element {
  const [actionLoading, setActionLoading] = useState(false)
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null)

  // Cached rather than a bare effect because this card is not always mounted
  // in the same place: the `settings-mail-beside-messaging` arm renders it
  // under a different parent, and a client-side flag resolves *after* the
  // first paint — so a test-arm visitor on a cold load unmounts the control
  // position and mounts the new one. With a per-mount fetch that is a second
  // subscription request and a second spinner on a card that had already
  // rendered; against the shared query cache it is a hit. A failed read still
  // renders as "no subscription", exactly as the effect's catch did.
  const { data: subscription = null, isPending: loading } = useQuery({
    queryKey: BILLING_SUBSCRIPTION_QUERY_KEY,
    queryFn: () => billingService.getSubscription(),
  })

  const handleManageSubscription = async () => {
    trackEvent(OnboardingEvents.MANAGE_SUBSCRIPTION_CLICKED)
    setActionLoading(true)
    try {
      const { url } = await billingService.createPortalSession(
        `${window.location.origin}/integrations`,
      )
      window.location.href = url
    } catch (error) {
      console.error('Failed to create portal session:', error)
      setActionLoading(false)
    }
  }

  const handleSubscribe = async () => {
    trackEvent(OnboardingEvents.SUBSCRIBE_CLICKED)
    setActionLoading(true)
    try {
      const { client_secret } = await billingService.createCheckoutSession(
        `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
      )
      setCheckoutClientSecret(client_secret ?? null)
    } catch (error) {
      console.error('Failed to create checkout session:', error)
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={CARD_CLASS}>
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900">Billing</h3>
        </div>
        <div className="flex flex-1 items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      </div>
    )
  }

  // Org members without their own Stripe subscription: billing belongs to the
  // team admin — even while the team plan is awaiting payment (see
  // routers/billing.py) — so never offer the individual checkout, nor a Stripe
  // portal they don't have. Members still paying on a live personal
  // subscription keep the normal manage/past-due flows below; team coverage
  // takes over when that subscription ends.
  //
  // Deliberately NOT utils/billing.ts's isTeamCovered: this asks the broader
  // presentation question (claim the team plan even while it is awaiting
  // payment, i.e. no is_active), whereas isTeamCovered asks whether a charge
  // is already covered and so must match the server's checkout guard. Adding
  // is_active here would put an awaiting-payment member back on the
  // "No Subscription / Subscribe" card (GET-9216).
  if (subscription?.billing_managed_by_team && subscription.stripe_subscription_id == null) {
    return (
      <div className={CARD_CLASS}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-900">Billing</h3>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
            Team plan
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          {subscription.is_active
            ? 'Your subscription is covered by your team.'
            : 'Team membership and billing are managed by your team admin.'}
        </p>
        <Link
          to="/team"
          className="mt-auto inline-flex items-center gap-2 self-start px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200"
        >
          <Users className="w-4 h-4" />
          View your team
        </Link>
      </div>
    )
  }

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900">Billing</h3>
        </div>
        <StatusBadge status={subscription?.status ?? null} />
      </div>

      {subscription?.status === 'trialing' && subscription.trial_end && (
        <p className="text-sm text-gray-600 mb-3">
          Trial ends on {new Date(subscription.trial_end).toLocaleDateString()}
        </p>
      )}

      {subscription?.cancel_at_period_end && subscription.current_period_end && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
          <p className="text-sm text-yellow-800">
            Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}.
          </p>
        </div>
      )}

      {isPastDue(subscription) && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
          <p className="text-sm text-orange-800">
            {PAST_DUE_NOTICE}
          </p>
        </div>
      )}

      {subscription?.billing_managed_by_team && subscription.stripe_subscription_id != null && (
        <p className="text-sm text-gray-500 mb-3">
          You're on your team's plan once this subscription ends — no action needed.
        </p>
      )}

      {/* One action row: the promo control is a sibling of the primary action,
          and this row owns the spacing between them — PromoCodeForm carries no
          margin of its own. mt-auto pins the row to the card's bottom edge. */}
      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-3">
        {subscription?.is_active ? (
          <button
            onClick={() => void handleManageSubscription()}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 disabled:opacity-50"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
            Manage Subscription
          </button>
        ) : (
          // past_due repairs via the portal, never a new checkout (see
          // utils/billing.ts) — same button shell, different action.
          <button
            onClick={() =>
              void (isPastDue(subscription)
                ? handleManageSubscription()
                : handleSubscribe())}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            {isPastDue(subscription) ? 'Update Payment Method' : 'Subscribe'}
          </button>
        )}

        {/* Stripe's portal has no "enter a code" for a live subscription, so
            this is where a code handed to an existing subscriber gets used. */}
        {canApplyPromoCode(subscription) && <PromoCodeForm />}
      </div>

      {checkoutClientSecret && (
        <CheckoutModal
          clientSecret={checkoutClientSecret}
          onClose={() => setCheckoutClientSecret(null)}
        />
      )}
    </div>
  )
}
